import {
  Building2,
  CreditCard,
  FileText,
  History,
  LayoutGrid,
  Map,
  Palette,
  Sparkles,
  Tags,
  Target,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import BingLogo from "~/assets/bing-webmaster-logo.svg";
import CloudflareLogo from "~/assets/cloudflare-logo.svg";
import Ga4Logo from "~/assets/google-analytics.svg";
import MerchantCenterLogo from "~/assets/google-merchant-center-logo.png";
import GscLogo from "~/assets/google-search-console.svg";
import SanityLogo from "~/assets/sanity-logo.svg";
import ShopifyLogo from "~/assets/shopify-logo.png";
import VercelLogo from "~/assets/vercel-logo.svg";
import WordPressLogo from "~/assets/wordpress-logo.svg";

export type SettingsItem = {
  title: string;
  path: string;
  icon?: LucideIcon;
  logo?: string;
  description: string;
  requiresAiVisibility?: boolean;
  requiresAiTrafficAnalytics?: boolean;
  hideForAgency?: boolean;
  requiresEcommerce?: boolean;
};

export type SettingsGroup = {
  title: string;
  items: SettingsItem[];
};

export const INTEGRATION_ITEMS: SettingsItem[] = [
  {
    title: "Cloudflare",
    path: "/settings/integrations/cloudflare",
    logo: CloudflareLogo,
    description: "AI Traffic Analytics for sites behind Cloudflare.",
    requiresAiTrafficAnalytics: true,
  },
  {
    title: "Vercel",
    path: "/settings/integrations/vercel",
    logo: VercelLogo,
    description: "AI Traffic Analytics for sites hosted on Vercel.",
    requiresAiTrafficAnalytics: true,
  },
  {
    title: "Google Search Console",
    path: "/settings/integrations/google-search-console",
    logo: GscLogo,
    description: "Bring search performance data into chat and reports.",
  },
  {
    title: "Google Analytics",
    path: "/settings/integrations/google-analytics",
    logo: Ga4Logo,
    description: "Bring web traffic data into chat and reports.",
  },
  {
    title: "Bing Webmaster Tools",
    path: "/settings/integrations/bing-webmaster-tools",
    logo: BingLogo,
    description: "Bring Bing search performance into chat and reports.",
  },
  {
    title: "WordPress",
    path: "/settings/integrations/wordpress",
    logo: WordPressLogo,
    description: "Publish content straight to your WordPress site.",
  },
  {
    title: "Sanity",
    path: "/settings/integrations/sanity",
    logo: SanityLogo,
    description: "Publish content straight to your Sanity dataset.",
  },
  {
    title: "Shopify",
    path: "/settings/integrations/shopify",
    logo: ShopifyLogo,
    description: "Import your Shopify product catalog.",
    requiresEcommerce: true,
  },
  {
    title: "Google Merchant Center",
    path: "/settings/integrations/google-merchant-center",
    logo: MerchantCenterLogo,
    description: "Import your Merchant Center product feed.",
    requiresEcommerce: true,
  },
  {
    title: "Product feed",
    path: "/settings/integrations/product-feed",
    icon: FileText,
    description: "Import products from a feed link or file.",
    requiresEcommerce: true,
  },
];

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    title: "Workspace",
    items: [
      {
        title: "General",
        path: "/settings/workspace/general",
        icon: Building2,
        description: "Workspace domain, plan and business location.",
      },
      {
        title: "Team",
        path: "/settings/workspace/team",
        icon: Users,
        description: "Invite teammates and manage access.",
      },
      {
        title: "Billing",
        path: "/settings/workspace/billing",
        icon: CreditCard,
        description: "Plan, invoices and payment method.",
        hideForAgency: true,
      },
    ],
  },
  {
    title: "Website",
    items: [
      {
        title: "Sitemaps",
        path: "/settings/website/sitemap",
        icon: Map,
        description: "Tell us which pages your site publishes.",
      },
      {
        title: "Competitors",
        path: "/settings/website/competitors",
        icon: Target,
        description: "Brands you want to be compared against.",
      },
      {
        title: "Brand Guidelines",
        path: "/settings/website/brand-guidelines",
        icon: Palette,
        description: "Voice and style rules the agent writes with.",
      },
    ],
  },
  {
    title: "Integrations",
    items: [
      {
        title: "All integrations",
        path: "/settings/integrations",
        icon: LayoutGrid,
        description: "Every connection in one place.",
      },
      ...INTEGRATION_ITEMS,
    ],
  },
  {
    title: "AI Visibility",
    items: [
      {
        title: "Tracked Platforms",
        path: "/settings/ai-visibility/tracked-platforms",
        icon: Sparkles,
        description: "The AI platforms we check your visibility on.",
      },
      {
        title: "Prompt Tags",
        path: "/settings/ai-visibility/prompt-tags",
        icon: Tags,
        description: "Group prompts so reporting stays readable.",
      },
      {
        title: "Run History",
        path: "/settings/ai-visibility/run-history",
        icon: History,
        description: "Every tracking run and what it produced.",
      },
    ],
  },
];

export const SETTINGS_ITEMS: SettingsItem[] = SETTINGS_GROUPS.flatMap(
  (group) => group.items
);

export function findSettingsItem(pathname: string): SettingsItem | null {
  const exact = SETTINGS_ITEMS.find((item) => item.path === pathname);
  if (exact) return exact;

  return (
    SETTINGS_ITEMS.filter((item) => item.path !== "/settings/integrations")
      .filter((item) => pathname.startsWith(`${item.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0] ?? null
  );
}

export function findSettingsGroup(pathname: string): SettingsGroup | null {
  const item = findSettingsItem(pathname);
  if (!item) return null;
  return SETTINGS_GROUPS.find((group) => group.items.includes(item)) ?? null;
}
